class VideoObserver extends HTMLElement {
  /** @type {number} */
  videoDuration = JSON.parse(this?.dataset?.videoDuration || '0');

  /** @type {HTMLVideoElement | null} */
  video = null;

  /** @type {IntersectionObserver | null} */
  observer = null;

  /** @type {{ threshold: number }} */
  options = {
    threshold: 0.25,
  };

  /** @type {HTMLButtonElement | null} */
  playButton = null;

  /** @type {HTMLButtonElement | null} */
  soundButton = null;

  /** @type {HTMLDivElement | null} */
  progress = null;

  /** @type {boolean} */
  receivedUserInteraction = false;

  /**
   * @method connectedCallback
   * @description Handles the connected callback
   * @returns {void}
   */
  connectedCallback() {
    const options = JSON.parse(this.dataset.options || '{}');

    this.options = {
      ...this.options,
      ...options,
    };

    this.video = this.querySelector('video');
    if (!this.video) return;
    if (this.video && 'IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(this.observerCallback, {
        threshold: this.options.threshold,
      });
      this.observer.observe(this.video);
    } else {
      this.play();
    }

    // Play/pause and mute/unmute buttons
    this.playButton = this.querySelector('[data-video-observer="play"]');
    this.playButton?.addEventListener('click', this.handlePlay);
    this.soundButton = this.querySelector('[data-video-observer="sound"]');
    this.soundButton?.addEventListener('click', this.handleSound);

    // Progress bar
    this.progress = this.querySelector('[data-video-observer="progress"]');
    if (!this.progress) return;
    this.video.addEventListener('timeupdate', (event) => {
      this.setProgressPercentage();
    });
  }

  /**
   * @method disconnectedCallback
   * @description Handles the disconnected callback
   * @returns {void}
   */
  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    this.playButton?.removeEventListener('click', this.handlePlay);
    this.soundButton?.removeEventListener('click', this.handleSound);
    this.video.removeEventListener('timeupdate', this.setProgressPercentage);
  }

  /**
   * @method observerCallback
   * @description Handles the intersection observer callback
   * @param {IntersectionObserverEntry[]} entries - The intersection observer entries
   * @returns {void}
   */
  observerCallback = (entries) => {
    entries.forEach((entry) => {
      // If the user has click on pause or play button, don't auto play or pause the video
      if (this.receivedUserInteraction) return;
      entry.isIntersecting ? this.play() : this.pause();
    });
  };

  /**
   * @method play
   * @description Plays the video
   * @returns {void}
   */
  play() {
    this.video?.play();
    this.playButton?.classList.add('active');
  }

  /**
   * @method pause
   * @description Pauses the video
   * @returns {void}
   */
  pause() {
    this.video?.pause();
    this.playButton?.classList.remove('active');
  }

  /**
   * @method handlePlay
   * @description Toggles play and pause for the video
   * @returns {void}
   */
  handlePlay = () => {
    this.video?.paused ? this.play() : this.pause();
    this.receivedUserInteraction = true;
  };

  /**
   * @method handleSound
   * @description Toggles mute and unmute for the video
   * @returns {void}
   */
  handleSound = () => {
    this.video.muted = !this.video.muted;
    this.video.muted
      ? this.soundButton?.classList.remove('active')
      : this.soundButton?.classList.add('active');
  };

  /**
   * @method setProgressPercentage
   * @description Sets the progress percentage for the video
   * @returns {void}
   */
  setProgressPercentage() {
    const percentage = 100 * ((this.video.currentTime * 1000) / this.videoDuration);
    this.progress.style.setProperty('--percentage', `${percentage}%`);
  }
}

customElements.define('video-observer', VideoObserver);
